import { ChainPath } from '@ankr.com/chains-list';
import { UserEndpointToken } from 'multirpc-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { IProjectWithBlockchains } from 'domains/projects/types';
import { getProjectPaths } from 'modules/projects/utils/getProjectPaths';
import { usePrivateChainsInfo } from 'hooks/usePrivateChainsInfo';

export type IProjectSubchains = Record<UserEndpointToken, ChainPath[]>;

export interface IUseProjectSubchainsProps {
  projectsWithBlockchains: IProjectWithBlockchains[];
}

export const useProjectSubchains = ({
  projectsWithBlockchains,
}: IUseProjectSubchainsProps) => {
  const { chains } = usePrivateChainsInfo();

  const initialSubchains: IProjectSubchains = useMemo(
    () =>
      projectsWithBlockchains?.reduce(
        (acc, { blockchains: projectBlockchains, userEndpointToken }) => ({
          ...acc,
          [userEndpointToken]: getProjectPaths({ chains, projectBlockchains }),
        }),
        {},
      ),
    [chains, projectsWithBlockchains],
  );

  const [projectSubchains, setProjectSubchains] = useState(initialSubchains);

  useEffect(() => {
    setProjectSubchains(initialSubchains);
  }, [initialSubchains]);

  const setSelectedSubchains = useCallback(
    (subchains: ChainPath[] = [], projectToken: UserEndpointToken) => {
      setProjectSubchains(currentProjectSubchains => ({
        ...currentProjectSubchains,
        [projectToken]: subchains,
      }));
    },
    [],
  );

  return { selectedSubchains: projectSubchains, setSelectedSubchains };
};
